import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../store/authStore';
import { AuthService } from '../../services/authService';
import { AdminKeyService } from '../../services/adminKeyService';

interface AdminAuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prefillAdminSecret?: string;
}

const AdminAuthModal: React.FC<AdminAuthModalProps> = ({ open, onClose, onSuccess, prefillAdminSecret }) => {
  const { logout, login } = useAuthStore();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    adminSecret: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!open) return;
    const v = String(prefillAdminSecret || '').trim();
    if (!v) return;
    setFormData((prev) => ({ ...prev, adminSecret: v }));
  }, [open, prefillAdminSecret]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setError('');
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      // Logout any existing user session first
      logout();

      AdminKeyService.clearLocalSecret();

      const ok = await AdminKeyService.verifyAnyKey(formData.adminSecret.trim());
      if (!ok) {
        setError('Invalid admin secret key.');
        setLoading(false);
        return;
      }

      let user: any = null;
      try {
        // Sign in with Supabase
        const res = await AuthService.signIn(formData.email, formData.password);
        user = res?.user;
      } catch (e: any) {
        try {
          const resp = await fetch('/api/admin-fallback.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'admin_fallback_login',
              email: formData.email,
              password: formData.password,
              adminSecret: formData.adminSecret.trim(),
            }),
          });
          const json = await resp.json().catch(() => null);
          if (!resp.ok || !json?.ok) {
            throw new Error(json?.error || e?.message || 'Login failed. Please check your credentials.');
          }
          user = json?.user;
        } catch (fallbackErr: any) {
          throw new Error(fallbackErr?.message || e?.message || 'Login failed. Please check your credentials.');
        }
      }

      if (!user?.isAdmin) {
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      // Transform user data for auth store (CRITICAL: This was missing!)
      const authUser = {
        id: user.id,
        email: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email.split('@')[0],
        firstName: user.firstName,
        lastName: user.lastName,
        role: 'admin' as const,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isAdmin: user.isAdmin,
        userStatusPreference: user.userStatusPreference,
      };

      // Update auth store with admin user
      login(authUser);

      AdminKeyService.setLocalSecret(formData.adminSecret.trim());

      setLoading(false);
      onSuccess();
    } catch (error: any) {
      setError(error.message || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setFormData({
      email: '',
      password: '',
      adminSecret: '',
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <AdminIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold">
            Admin Portal
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
          Access the administrative dashboard
        </Typography>
        <Alert 
          severity="info" 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.1)', 
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            }
          }}
        >
          <Typography variant="body2">
            <strong>Admin Access:</strong> Sign in with your admin credentials. You will be logged out of your current user session.
          </Typography>
        </Alert>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2.25,
              py: 1,
              borderRadius: 999,
              background: `linear-gradient(180deg, ${alpha('#ffffff', 0.14)} 0%, ${alpha('#ffffff', 0.08)} 100%)`,
              border: `1px solid ${alpha('#ffffff', 0.22)}`,
            }}
          >
            <LoginIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontWeight: 800, letterSpacing: 0.2 }}>Login</Typography>
          </Box>
        </Box>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Alert 
              severity={error.includes('successful') ? 'success' : 'error'} 
              sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
            >
              {error}
            </Alert>
          </motion.div>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              placeholder="admin@example.com"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': {
                    color: 'white',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                },
              }}
            />
            <TextField
              fullWidth
              label="Admin Secret Key"
              type="password"
              value={formData.adminSecret}
              onChange={handleInputChange('adminSecret')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': {
                    color: 'white',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                },
              }}
            />
            
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange('password')}
              placeholder="123"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'rgba(255,255,255,0.7)' }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-focused': {
                    color: 'white',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                },
              }}
            />
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Enter your admin credentials to access the dashboard
              </Typography>
            </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleLogin}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
          sx={{
            bgcolor: 'white',
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            px: 3,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.9)',
            },
            '&:disabled': {
              bgcolor: 'rgba(255,255,255,0.5)',
              color: 'rgba(0,0,0,0.3)',
            },
          }}
        >
          {loading ? 'Processing...' : 'Login'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminAuthModal;
